import type { ComponentType, ReactNode } from 'react'
import { useLingui } from '@lingui/react/macro'
import { ArrowUpDownIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Appear } from '@/components/custom/appear'
import { NAVIGATION_ICONS, SparklesIcon } from '../shared/navigation-config'

/**
 * The company welcome's body, shared by the dialog (welcome-dialog.tsx) and its pending skeleton, so the skeleton's
 * cards are exactly as tall as the dialog's at any width and in any locale: it is as tall as its content, and the
 * whole dialog scrolls in the viewport. Kept light on purpose, since the route's pendingComponent is eager.
 */

/** The dialog's own box: no height cap, so the viewport scrolls it (ui/dialog-surface.ts). */
export const WELCOME_DIALOG_CLASS = 'flex flex-col gap-0 p-0 sm:max-w-lg'

/** The actions stick to the viewport's bottom while a tall welcome scrolls under them. */
export const WELCOME_FOOTER_CLASS = 'bg-background sticky bottom-0 z-10 flex flex-col gap-2 rounded-b-xl px-6 py-4'

// Per-entity icon tints, kept in sync with the section colors used across the app (CATEGORY_CONFIG in
// shared/profile-selector.tsx and the avatar palette): jobs=blue, teams=violet,
// employees=emerald, offers=indigo. Static class strings so Tailwind keeps them.
const ENTITY_STYLE = {
  jobs: 'bg-blue-500/10 text-blue-500',
  teams: 'bg-violet-500/10 text-violet-500',
  employees: 'bg-emerald-500/10 text-emerald-500',
  offers: 'bg-indigo-500/10 text-indigo-500',
} as const

const STAGGER = 120

/** The welcome's heading, its two sections of cards; `skeleton` draws each card as its own box, sized by its text. */
export function WelcomeContent(props: { heading: ReactNode; skeleton?: boolean }) {
  const { t } = useLingui()
  // The three building blocks, in the recommended order — each card says what to do in that section.
  const steps = [
    {
      icon: NAVIGATION_ICONS.job,
      style: ENTITY_STYLE.jobs,
      label: t`Jobs`,
      hint: t`Create all the positions in your organization and define the ideal psychographic profile for each one.`,
    },
    {
      icon: NAVIGATION_ICONS.team,
      style: ENTITY_STYLE.teams,
      label: t`Teams`,
      hint: t`Add the existing teams in your company.`,
    },
    {
      icon: NAVIGATION_ICONS.employee,
      style: ENTITY_STYLE.employees,
      label: t`Employees`,
      hint: t`Add your employees and assign each one their corresponding position. If you wish, you can also add them to one or more teams. Start by adding yourself, using your employee email address.`,
    },
  ]
  const notes: {
    icon: ComponentType<{ className?: string; strokeWidth?: number }>
    style?: string
    title?: string
    lines: string[]
  }[] = [
    {
      icon: ArrowUpDownIcon,
      lines: [t`This is the order we suggest, but you can use whatever order suits you best.`],
    },
    {
      icon: SparklesIcon,
      title: t`When you finish...`,
      lines: [
        t`Each employee will receive an invitation to complete their Psychographic Profile Test.`,
        t`From that moment on, D'Anchiano will automatically start analyzing the information and let you generate valuable insights about the talent in your organization.`,
        t`The more profiles are completed, the more value you will get.`,
      ],
    },
    {
      icon: NAVIGATION_ICONS.offer,
      style: ENTITY_STYLE.offers,
      title: t`Offers`,
      lines: [t`Optionally, you can also create offers and invite candidates to each of them.`],
    },
  ]

  return (
    <div className="flex flex-col gap-7 px-6 py-8">
      {props.heading}

      {/* GETTING STARTED — the three basics every organization needs, as numbered steps */}
      <section className="flex flex-col gap-3">
        <div className="flex flex-col gap-1 px-1">
          <p className="text-muted-foreground text-sm">
            {t`To get started, we need three basic elements of your organization. We recommend following this order:`}
          </p>
        </div>
        <ul className="flex flex-col gap-3">
          {steps.map((item, index) => {
            const Icon = item.icon
            return (
              <WelcomeCard
                key={item.label}
                skeleton={props.skeleton}
                delay={index * STAGGER}
                tight
                title={item.label}
                lines={[item.hint]}
                media={
                  <>
                    <div className={cn('flex size-12 items-center justify-center rounded-xl', item.style)}>
                      <Icon className="size-6" />
                    </div>
                    <span className="bg-foreground text-background absolute -top-2 -left-2 flex size-6 items-center justify-center rounded-full text-xs font-semibold">
                      {index + 1}
                    </span>
                  </>
                }
              />
            )
          })}
        </ul>
      </section>

      {/* KEEP IN MIND — flexible order, what happens next, and the optional offers section */}
      <section className="flex flex-col gap-3">
        <span className="text-muted-foreground px-1 text-xs font-semibold tracking-wider uppercase">
          {t`Keep in mind`}
        </span>
        <ul className="flex flex-col gap-3">
          {notes.map((note, index) => {
            const Icon = note.icon
            return (
              <WelcomeCard
                key={note.lines[0]}
                skeleton={props.skeleton}
                delay={(steps.length + index) * STAGGER}
                title={note.title}
                lines={note.lines}
                media={
                  <div
                    className={cn(
                      'flex size-12 items-center justify-center rounded-xl',
                      note.style ?? 'bg-muted text-foreground/70',
                    )}
                  >
                    <Icon className="size-6" strokeWidth={1.5} />
                  </div>
                }
              />
            )
          })}
        </ul>
      </section>
    </div>
  )
}

const CARD_CLASS = 'flex items-start gap-4 rounded-xl border p-4'

/**
 * One card: its media box, then its title and lines. As a skeleton the text is laid out invisibly through
 * `SizerText`, so the box wraps as the card does; the cards rise in one by one, so the text is not painted early.
 */
function WelcomeCard(props: {
  skeleton?: boolean
  delay: number
  media: ReactNode
  title?: string
  lines: string[]
  /** The steps' title sits closer to its hint than a note's. */
  tight?: boolean
}) {
  const text = (value: string, className?: string) =>
    props.skeleton ? (
      <SizerText key={value} text={value} className={className} />
    ) : (
      <span key={value} className={className}>
        {value}
      </span>
    )
  const body = (
    <>
      <div className="relative size-12 shrink-0">{!props.skeleton && props.media}</div>
      <div className={cn('flex flex-col', props.tight ? 'gap-0.5' : 'gap-1')}>
        {props.title && text(props.title, 'font-semibold')}
        <span className="text-muted-foreground flex flex-col gap-1 text-sm">
          {props.lines.map((line) => text(line))}
        </span>
      </div>
    </>
  )
  if (props.skeleton) {
    return (
      <li data-slot="skeleton" className={cn(CARD_CLASS, 'bg-muted animate-pulse border-transparent')}>
        {body}
      </li>
    )
  }
  return (
    <Appear as="li" delay={props.delay} className={cn(CARD_CLASS, 'bg-card')}>
      {body}
    </Appear>
  )
}

/** Takes the room `text` would, with no text node: a pseudo-element's content, hidden. */
function SizerText(props: { text: string; className?: string }) {
  return (
    <span
      aria-hidden
      data-text={props.text}
      className={cn('invisible before:content-[attr(data-text)]', props.className)}
    />
  )
}
